import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "bg-white border border-gray-200 rounded-lg p-3 sm:p-6 mb-6" }
const _hoisted_2 = { class: "font-bold text-black mb-4" }
const _hoisted_3 = { class: "bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4" }
const _hoisted_4 = {
  key: 0,
  class: "text-xs font-medium text-black mb-2"
}
const _hoisted_5 = {
  id: "ContainerInputColis",
  class: "space-y-4"
}
const _hoisted_6 = { class: "flex items-center justify-between mb-3" }
const _hoisted_7 = { class: "font-medium text-black" }
const _hoisted_8 = ["onClick", "disabled"]
const _hoisted_9 = { class: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" }
const _hoisted_10 = ["for"]
const _hoisted_11 = ["id", "value", "onInput", "readonly"]
const _hoisted_12 = ["for"]
const _hoisted_13 = ["id", "value", "onInput", "readonly"]
const _hoisted_14 = ["for"]
const _hoisted_15 = ["id", "value", "onInput", "readonly"]
const _hoisted_16 = ["for"]
const _hoisted_17 = ["id", "value", "onInput", "readonly", "max"]
const _hoisted_18 = {
  key: 0,
  class: "mt-3 text-sm font-medium text-red-600"
}
const _hoisted_19 = { class: "mt-4 flex gap-3" }
const _hoisted_20 = ["disabled"]

import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useCustomsDeclarationStore } from "@/stores/customsDeclaration";
import ParcelWeightWarning from "@/components/order/ParcelWeightWarning.vue";
import { useNotification } from "@/composables/useNotification";
import {
  getCarrierDimensionLimits,
  getDimensionLimitsHint,
  validateColisDimensions,
} from "@/utils/parcelDimensionsLimits";


export default /*@__PURE__*/_defineComponent({
  __name: 'ParcelDimensions',
  props: {
    orderId: {},
    orderStatus: {},
    nbColis: {},
    idLivraison: {},
    countryCode: {},
    postalCode: {},
    title: {},
    calculatedWeight: {}
  },
  emits: ["update:valid", "colis-count-changed", "weight-changed"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Utiliser le store Pinia étendu
const customsStore = useCustomsDeclarationStore();

// État dérivé du store
const colisList = computed(() =>
  customsStore.getParcelDimensions(props.orderId),
);
const isSaving = ref(false);
const { error: showError } = useNotification();
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

const dimensionLimitsHint = computed(() =>
  getDimensionLimitsHint(props.idLivraison ?? null, props.countryCode, props.postalCode),
);
const dimensionLimits = computed(() =>
  getCarrierDimensionLimits(props.idLivraison ?? null, props.countryCode, props.postalCode),
);
const dimensionFieldClass = (
  colis: (typeof colisList.value)[number],
  isWeight = false,
) =>
  isWeight
    ? colis.poids != null && colis.poids > dimensionLimits.value.poids
      ? "border-red-500 focus:ring-red-500"
      : "border-gray-300 focus:ring-blue-500"
    : colis.longueur != null &&
        colis.largeur != null &&
        colis.hauteur != null &&
        validateColisDimensions(
          colis.longueur,
          colis.largeur,
          colis.hauteur,
          dimensionLimits.value,
        )
    ? "border-red-500 focus:ring-red-500"
    : "border-gray-300 focus:ring-blue-500";
const parcelError = (colis: (typeof colisList.value)[number]) => {
  const { longueur, largeur, hauteur, poids } = colis;
  if (
    longueur == null ||
    largeur == null ||
    hauteur == null ||
    poids == null
  )
    return "Toutes les dimensions et le poids sont obligatoires.";
  if (longueur <= 0 || largeur <= 0 || hauteur <= 0 || poids <= 0)
    return "Toutes les dimensions et le poids doivent être supérieurs à zéro.";
  const dimensionsError = validateColisDimensions(
    longueur,
    largeur,
    hauteur,
    dimensionLimits.value,
  );
  if (dimensionsError) return `Gabarit dépassé : ${dimensionsError}.`;
  if (poids > dimensionLimits.value.poids)
    return `Poids ${poids} kg (maximum ${dimensionLimits.value.poids} kg).`;
  return "";
};
// Éditable en attente (1/9), confirmée (4) et pickup groupage (7/8)
const canEdit = computed(() => {
  const status = Number(props.orderStatus);
  return status === 1 || status === 9 || status === 4 || status === 7 || status === 8;
});

// Validation bloquante : champs obligatoires et gabarit eFashion respecté.
const dimensionsValid = computed(() => {
  const list = colisList.value;
  if (!list || list.length === 0) return false;
  const limits = dimensionLimits.value;
  return list.every((colis) => {
    const l = colis.longueur;
    const w = colis.largeur;
    const h = colis.hauteur;
    const p = colis.poids;
    if (
      l == null ||
      l <= 0 ||
      w == null ||
      w <= 0 ||
      h == null ||
      h <= 0 ||
      p == null ||
      p <= 0
    )
      return false;
    return (
      p <= limits.poids && validateColisDimensions(l, w, h, limits) === null
    );
  });
});

const addColis = async () => {
  await customsStore.addParcel(props.orderId);
  emit("colis-count-changed", colisList.value.length);
};

const removeColis = async (index: number) => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    await customsStore.removeParcel(props.orderId, index);
    emit("colis-count-changed", colisList.value.length, index);
  } catch (error) {
    showError(error instanceof Error ? error.message : "Erreur lors de la suppression");
  } finally {
    isSaving.value = false;
  }
};

const blockNonNumeric = (event: KeyboardEvent) => {
  const allowed = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ];
  if (allowed.includes(event.key)) return;
  if (
    (event.ctrlKey || event.metaKey) &&
    ["a", "c", "v", "x"].includes(event.key)
  )
    return;
  if (event.key === "." || event.key === ",") return;
  if (event.key < "0" || event.key > "9") {
    event.preventDefault();
  }
};

const handlePaste = (event: ClipboardEvent) => {
  const paste = event.clipboardData?.getData("text") || "";
  if (!/^\d*\.?\d*$/.test(paste)) {
    event.preventDefault();
  }
};

const handleDimensionChange = (
  index: number,
  dimension: "longueur" | "largeur" | "hauteur" | "poids",
  event: Event,
) => {
  const target = event.target as HTMLInputElement | null;
  if (target) {
    const value = target.value ? Number(target.value) : null;
    customsStore.updateParcelDimension(props.orderId, index, dimension, value);
    if (dimension === "poids") emit("weight-changed", index, value);
  }
};

// Initialiser le store au montage
onMounted(() => {
  // Si pas de dimensions dans le store, les initialiser
  if (!customsStore.hasParcelDimensions(props.orderId)) {
    customsStore.initializeParcelDimensions(props.orderId, props.nbColis || 1);
  }
});

// Réinitialiser si les props changent
watch(
  [() => props.orderId, () => props.orderStatus],
  ([newOrderId, newOrderStatus]) => {
    if (newOrderId && newOrderStatus !== undefined) {
      if (!customsStore.hasParcelDimensions(newOrderId)) {
        customsStore.initializeParcelDimensions(newOrderId, props.nbColis || 1);
      }
    }
  },
);

watch(
  dimensionsValid,
  (val) => {
    emit("update:valid", val);
  },
  { immediate: true },
);

watch(
  () => colisList.value.map(({ longueur, largeur, hauteur, poids }) => [
    longueur,
    largeur,
    hauteur,
    poids,
  ]),
  () => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
      autoSaveTimer = null;
    }
    if (!canEdit.value || !dimensionsValid.value) return;
    autoSaveTimer = setTimeout(async () => {
      autoSaveTimer = null;
      isSaving.value = true;
      try {
        await customsStore.saveDimensionsToDB(props.orderId);
      } catch (error) {
        showError(error instanceof Error ? error.message : "Impossible d’enregistrer les dimensions");
      } finally {
        isSaving.value = false;
      }
    }, 500);
  },
  { deep: true },
);

onBeforeUnmount(() => {
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h4", _hoisted_2, _toDisplayString(_ctx.title || "Préparation du groupage"), 1),
    _createElementVNode("div", _hoisted_3, [
      _cache[9] || (_cache[9] = _createElementVNode("div", { class: "flex items-center justify-between mb-4" }, [
        _createElementVNode("h5", { class: "font-semibold text-black" }, [
          _createElementVNode("i", { class: "fas fa-ruler-combined mr-2 text-blue-600" }),
          _createTextVNode(" Dimensions des colis ")
        ])
      ], -1)),
      _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-sm text-black mb-4" }, " Veuillez renseigner les dimensions en centimètres (cm) et le poids pour chaque colis. Tous les champs sont obligatoires. Si un colis dépasse le gabarit indiqué, répartissez son contenu dans deux colis ou plus. ", -1)),
      (dimensionLimitsHint.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(dimensionLimitsHint.value), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_5, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(colisList.value, (colis, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: `colis-${index}-${colisList.value.length}`,
            class: "divColis bg-white border border-gray-300 rounded-lg p-3 sm:p-4"
          }, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("span", _hoisted_7, " Colis n°" + _toDisplayString(index + 1), 1),
              (index > 0 && canEdit.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    onClick: _withModifiers(($event: any) => (removeColis(index)), ["stop"]),
                    disabled: isSaving.value,
                    class: "px-3 py-1.5 bg-red-100 text-red-600 rounded-md hover:bg-red-200 disabled:bg-gray-200 disabled:text-black disabled:cursor-not-allowed transition-colors text-sm font-medium",
                    type: "button",
                    title: "Supprimer ce colis"
                  }, [
                    _createElementVNode("i", {
                      class: _normalizeClass(["fas fa-trash-alt mr-1", { 'fa-spin': isSaving.value }])
                    }, null, 2),
                    _createTextVNode(" " + _toDisplayString(isSaving.value ? "Suppression..." : "Supprimer"), 1)
                  ], 8, _hoisted_8))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", null, [
                _createElementVNode("label", {
                  for: `colis-${index}-longueur`,
                  class: "block text-sm font-medium text-black mb-1"
                }, _cache[4] || (_cache[4] = [
                  _createTextVNode(" Longueur (cm) "),
                  _createElementVNode("span", { class: "text-red-600" }, "*", -1)
                ]), 8, _hoisted_10),
                _createElementVNode("input", {
                  id: `colis-${index}-longueur`,
                  value: colis.longueur,
                  onInput: (e) => handleDimensionChange(index, 'longueur', e),
                  onKeydown: blockNonNumeric,
                  onPaste: handlePaste,
                  onWheel: _cache[0] || (_cache[0] = (e) => (e.target as HTMLInputElement).blur()),
                  type: "number",
                  class: _normalizeClass(["numbersonly longueur w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2", dimensionFieldClass(colis)]),
                  placeholder: "longueur",
                  readonly: !canEdit.value,
                  min: "0",
                  step: "0.1"
                }, null, 42, _hoisted_11)
              ]),
              _createElementVNode("div", null, [
                _createElementVNode("label", {
                  for: `colis-${index}-largeur`,
                  class: "block text-sm font-medium text-black mb-1"
                }, _cache[5] || (_cache[5] = [
                  _createTextVNode(" Largeur (cm) "),
                  _createElementVNode("span", { class: "text-red-600" }, "*", -1)
                ]), 8, _hoisted_12),
                _createElementVNode("input", {
                  id: `colis-${index}-largeur`,
                  value: colis.largeur,
                  onInput: (e) => handleDimensionChange(index, 'largeur', e),
                  onKeydown: blockNonNumeric,
                  onPaste: handlePaste,
                  onWheel: _cache[1] || (_cache[1] = (e) => (e.target as HTMLInputElement).blur()),
                  type: "number",
                  class: _normalizeClass(["numbersonly largeur w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2", dimensionFieldClass(colis)]),
                  placeholder: "largeur",
                  readonly: !canEdit.value,
                  min: "0",
                  step: "0.1"
                }, null, 42, _hoisted_13)
              ]),
              _createElementVNode("div", null, [
                _createElementVNode("label", {
                  for: `colis-${index}-hauteur`,
                  class: "block text-sm font-medium text-black mb-1"
                }, _cache[6] || (_cache[6] = [
                  _createTextVNode(" Hauteur (cm) "),
                  _createElementVNode("span", { class: "text-red-600" }, "*", -1)
                ]), 8, _hoisted_14),
                _createElementVNode("input", {
                  id: `colis-${index}-hauteur`,
                  value: colis.hauteur,
                  onInput: (e) => handleDimensionChange(index, 'hauteur', e),
                  onKeydown: blockNonNumeric,
                  onPaste: handlePaste,
                  onWheel: _cache[2] || (_cache[2] = (e) => (e.target as HTMLInputElement).blur()),
                  type: "number",
                  class: _normalizeClass(["numbersonly hauteur w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2", dimensionFieldClass(colis)]),
                  placeholder: "hauteur",
                  readonly: !canEdit.value,
                  min: "0",
                  step: "0.1"
                }, null, 42, _hoisted_15)
              ]),
              _createElementVNode("div", null, [
                _createElementVNode("label", {
                  for: `colis-${index}-poids`,
                  class: "block text-sm font-medium text-black mb-1"
                }, _cache[7] || (_cache[7] = [
                  _createTextVNode(" Poids (kg) "),
                  _createElementVNode("span", { class: "text-red-600" }, "*", -1)
                ]), 8, _hoisted_16),
                _createElementVNode("input", {
                  id: `colis-${index}-poids`,
                  value: colis.poids,
                  onInput: (e) => handleDimensionChange(index, 'poids', e),
                  onKeydown: blockNonNumeric,
                  onPaste: handlePaste,
                  onWheel: _cache[3] || (_cache[3] = (e) => (e.target as HTMLInputElement).blur()),
                  type: "number",
                  class: _normalizeClass(["numbersonly poids w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2", dimensionFieldClass(colis, true)]),
                  placeholder: "poids",
                  readonly: !canEdit.value,
                  min: "0",
                  max: dimensionLimits.value.poids,
                  step: "0.01"
                }, null, 42, _hoisted_17)
              ])
            ]),
            (parcelError(colis))
              ? (_openBlock(), _createElementBlock("p", _hoisted_18, [
                  _cache[8] || (_cache[8] = _createElementVNode("i", { class: "fas fa-exclamation-circle mr-1" }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(parcelError(colis)), 1)
                ]))
              : _createCommentVNode("", true)
          ]))
        }), 128))
      ]),
      _createVNode(ParcelWeightWarning, {
        "show-comparison": "",
        "calculated-weight": _ctx.calculatedWeight,
        parcels: colisList.value
      }, null, 8, ["calculated-weight", "parcels"]),
      _createElementVNode("div", _hoisted_19, [
        (canEdit.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: addColis,
              disabled: isSaving.value,
              class: "px-4 py-2 border border-blue-500 rounded-md text-blue-600 hover:border-blue-700 hover:text-blue-700 transition-colors disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed text-sm font-medium",
              type: "button"
            }, [
              _createElementVNode("i", {
                class: _normalizeClass(["fas fa-plus mr-2", { 'fa-spin': isSaving.value }])
              }, null, 2),
              _createTextVNode(" " + _toDisplayString(isSaving.value ? "Sauvegarde..." : "Ajouter un colis"), 1)
            ], 8, _hoisted_20))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})